import { Link } from '@tanstack/react-router';
import { Building2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { CreateStoreDialog } from '../components/create-store-dialog';
import { StoreStatusBadge } from '../components/store-status-badge';
import { ViewOnStorefrontButton } from '../components/view-on-storefront-button';
import { useStores } from '../hooks/use-stores';

function StoresList() {
  const { t } = useTranslation('stores');
  const query = useStores();
  const stores = query.data ?? [];
  const create = (tour?: boolean) => (
    <Can permission="stores.write">
      <CreateStoreDialog tour={tour} />
    </Can>
  );

  return (
    <PageContent>
      <PageHeader title={t('title')} description={t('description')} actions={create(true)} />
      {query.isPending ? (
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : stores.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {stores.map((store, index) => (
            <Card
              key={store.id}
              className="gap-4"
              data-tour={index === 0 ? 'org.stores.card' : undefined}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="truncate">{store.name}</span>
                  <StoreStatusBadge status={store.status} />
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <code className="text-xs">{store.slug}</code>
                  <span aria-hidden>·</span>
                  <span>{store.defaultCurrency}</span>
                  <span aria-hidden>·</span>
                  <span>{store.countryCode}</span>
                  <span aria-hidden>·</span>
                  <span>{store.timezone}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <dl className="text-muted-foreground grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                  <dt>{t('fields.primaryDomain')}</dt>
                  <dd className="text-foreground truncate">{store.primaryDomain ?? '—'}</dd>
                  <dt>{t('card.orders')}</dt>
                  <dd className="text-foreground font-mono">
                    {store.orderNumberPrefix}
                    {store.nextOrderNumber}
                  </dd>
                </dl>
                <div className="flex gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    data-tour={index === 0 ? 'org.stores.open' : undefined}
                  >
                    <Link to="/s/$storeId" params={{ storeId: store.id }}>
                      {t('open')}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    data-tour={index === 0 ? 'org.stores.settings' : undefined}
                  >
                    <Link to="/org/stores/$storeId" params={{ storeId: store.id }}>
                      <Settings aria-hidden />
                      {t('settings')}
                    </Link>
                  </Button>
                  {store.storefrontUrl ? (
                    <ViewOnStorefrontButton
                      url={store.storefrontUrl}
                      live
                      label={t('storefront.open')}
                      variant="ghost"
                    />
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Building2}
          title={t('empty.title')}
          description={t('empty.description')}
          action={create()}
          tour="org.stores.empty"
          tourLink
        />
      )}
    </PageContent>
  );
}

export function StoresPage() {
  return (
    <FeatureErrorBoundary name="stores.list">
      <StoresList />
    </FeatureErrorBoundary>
  );
}
